import { isSSR } from '../../../common/utils';
import { getBoolFromBitValue } from '../../../common/utils/helpers';
import { ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY } from '../../../constants';
import type { ConsentToken } from '../../../services/consent-hub-service/types';
import {
	checkIfConsentsAreStale,
	checkIfTokenIsDefault,
	hasLegacyV2TokenConsentedPreviously,
} from '../../../services/consent-hub-service/utils';
import { getCookie } from '../../cookie-controls/get-cookie';
import { checkTokenVersionValidity } from '../../cookie-controls/transformer';

export type BannerVizArgs = {
	consentToken: ConsentToken | string;
	consentTimestamp?: string;
	fallbackConsentToken?: string;
	currentLocale?: string; // This can still be passed from legacy SSR implementations, but we don't process it
};

const getUserIsAuthenticatedBit = (consentToken: ConsentToken) => {
	const { isV002 } = checkTokenVersionValidity(consentToken);

	let userIsAuthenticatedBit = consentToken[8]; // V3 position

	if (isV002) {
		// Schema has changed in V3, V2 had this bit in a different position. Logged out users will have this bit set to 1
		const legacyV2ConsentDataUnavailableBit = consentToken[7];
		// We can backfill the UserIsAuthenticated bit by flipping the bit value. If ConsentHub was unavailable, that means they were logged out.
		userIsAuthenticatedBit = legacyV2ConsentDataUnavailableBit === '1' ? '0' : '1';
	}

	return userIsAuthenticatedBit;
};

const shouldShowBannerForLoggedOutDefaultToken = (fallbackConsentToken?: ConsentToken) => {
	// Logged out + default consents + fallback: we have a discrepancy where ConsentHub is down/unavailable, or they're just logged out,
	// but the user has actually consented previously (which essentially the only way to save to the fallback to begin with)
	if (fallbackConsentToken) {
		const fallbackUserIsAuthenticatedBit = getUserIsAuthenticatedBit(fallbackConsentToken);
		const isLoggedOutFallbackConsents = !getBoolFromBitValue(fallbackUserIsAuthenticatedBit);

		const fallbackTokenHasGenuineConsents = !checkIfTokenIsDefault(fallbackConsentToken);

		const localOnlyUsersHasGenuineConsents =
			isLoggedOutFallbackConsents && fallbackTokenHasGenuineConsents;

		// If the fallback is from a logged out consent (localOnlyConsent), they're currently
		// logged out and also saved while logged out, so don't show the banner
		if (localOnlyUsersHasGenuineConsents) {
			return !fallbackTokenHasGenuineConsents;
		}

		const loggedOutUserPreviouslyConsentedWhileLoggedIn =
			!isLoggedOutFallbackConsents && fallbackTokenHasGenuineConsents;

		if (loggedOutUserPreviouslyConsentedWhileLoggedIn) {
			// Or if it is from a logged in consent, and they've consented somewhere (they must have)
			// then we should not show the banner, as their consents aren't "stale", we're just in a situation
			// where the original token has expired so they get a default set, but we should hide the banner
			return false;
		}
	}

	// Defaults consents + unauthed + missing a fallback, show the banner, as they haven't consented in the past year
	return true;
};

const shouldShowBannerForLoggedOutUser = ({
	consentToken,
	fallbackConsentToken,
}: BannerVizArgs) => {
	const tokenIsADefaultConsent = !!checkIfTokenIsDefault(consentToken);

	// Handle default token flow
	if (tokenIsADefaultConsent) {
		return shouldShowBannerForLoggedOutDefaultToken(fallbackConsentToken);
	}

	// Handle genuine token flow
	// If they're a legacy/V2 user who hasn't consented, this will have checked the V3 "is default" bit to note they've consented or not
	const localOnlyUserHasConsented =
		hasLegacyV2TokenConsentedPreviously(consentToken) || !tokenIsADefaultConsent;

	if (localOnlyUserHasConsented) {
		// LocalOnly users will never have a timestamp to validate against since they can't talk to ConsentHub
		// Existence of fallback consents means they've consented in the past year and do not have stale consents, do not show banner
		return fallbackConsentToken ? false : true;
	}

	// This case should never be hit: if it's not a default, but they haven't consented... what is it? Add a catch-all "show banner"
	return true;
};

const shouldShowBannerForLoggedInDefaultToken = (fallbackConsentToken?: ConsentToken) => {
	// Defaults + fallback + logged in, but they consented while they were previously logged out:
	if (fallbackConsentToken) {
		const fallbackUserIsAuthenticatedBit = getUserIsAuthenticatedBit(fallbackConsentToken);
		const isLoggedOutFallbackConsents = !getBoolFromBitValue(fallbackUserIsAuthenticatedBit);

		const fallbackTokenHasGenuineConsents = !checkIfTokenIsDefault(fallbackConsentToken);

		if (fallbackTokenHasGenuineConsents) {
			// Defaults + logged in user + consented <1yr ago, but while logged out
			if (isLoggedOutFallbackConsents) {
				/*
        They've consented, and thus stored a fallback while logged out (fallback's CH bit is 1),
        then fetched from CH but get empty data (since they couldn't save to CH on their initial "save"), which sets authed consent defaults (bit 0)

        UX Problem:
        Since they have genuinely consented within the past year, the ideal UX would be to not show the banner...
        But if we do not re-prompt here in an effort to recapture their consents, the opportunity for them/us to actually get consents in the DB
        is infrequent (either via Account Privacy Page or once this fallback expires in a year).

        Example:
        While logged out, they consent in Trello, so we store the unauthed fallback token, then log in on Trello. If we hide the banner when the
        fallback exists, if that user later goes to Confluence's cookie domain (where they have no fallback), it would show the banner
        because they have no stored consents on CH, and no fallback in that domain, but are logged in. Repeat for every new domain...

        We could be fancy and save under the hood for them if they have a fallback, but to ensure we don't copy stale/different consents saved in a fallback
        to ConsentHub on their behalf, we should just re-prompt them for consents now that they're logged in. This way, they will actually
        persist their choices, and when loading other domains/products, there will be a Source of Truth to reference and not show the banner.

        Ultimately, this feels like the more "correct" way to handle this situation, as the cost of showing the banner again to officially capture
        consents feels lower than the potential cost of relying on fallbacks in all situations/across all domains for this user for the next whole year

        Result: Show the banner again and hope we officially recapture consents this time around
      */

				return true;
			}
			// Defaults + logged in user + consented <1yr ago, but while logged in
			// This scenario shouldn't happen, or if it does, it's in a phase where we haven't fetched preferences
			// yet, so hide the banner for now
			return false;
		}

		// Default token + default fallback (authed or unauthed) mean we've likely stored this
		// fallback from SSR's initialPreferences flow in. Fallbacks should not be saved with defaults intentionally
		// But sometimes we copy an existing token to the fallback via SSR flow, so let's treat this
		// as an bunk fallback, and show the banner as they haven't consented
		return true;
	}

	// Default consent token + logged in user + no fallback = they haven't consented in the past year, show banner
	return true;
};

const shouldShowBannerForLoggedInUser = ({
	consentToken,
	consentTimestamp,
	fallbackConsentToken,
}: BannerVizArgs) => {
	// Handle default token flow
	const tokenIsADefaultConsent = !!checkIfTokenIsDefault(consentToken);
	if (tokenIsADefaultConsent) {
		return shouldShowBannerForLoggedInDefaultToken(fallbackConsentToken);
	}

	// Handle genuine token flow
	if (consentTimestamp) {
		// If the user has genuine consents, and a timestamp, check if they're stale (> 1yr old), show the banner if they're stale
		const showBannerIfStale = checkIfConsentsAreStale(consentTimestamp);
		return showBannerIfStale;
	}

	// Genuine consents + no timestamp— we're in a situation where we've been passed cached cookie data instead of CH response data,
	// so the only way to determine staleness is through the absence of the fallback.
	// Technically, we could assume since they've consented recently, we don't have to show the banner here but... to be safe we should
	return fallbackConsentToken ? false : true;
};

export const getBannerVisibility = ({
	consentToken,
	consentTimestamp,
	fallbackConsentToken: fallbackConsents,
}: BannerVizArgs) => {
	// Consent tokens are either a set of defaults, or genuine (persisted to the remote, or saved in an entire local-only flow via fallbacks).
	// the deciding factor is whether or not your consents are stale/fresh, either via the existence/absence of a fallback (a.k.a consented within a year or not)
	// or if the ConsentHub response data of a user's consent has a timestamp that is older than a year

	const fallbackConsentToken = isSSR()
		? fallbackConsents // When coming from SSR, we don't want to invoke `getCookie` since it will error
		: fallbackConsents || getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY);

	let userIsAuthenticatedBit = getUserIsAuthenticatedBit(consentToken);

	const isLoggedOutConsentToken = !getBoolFromBitValue(userIsAuthenticatedBit);

	let isBannerVisible = true; // By default, show the banner to every user

	// Unauthed/logged out user flow, should never receive a timestamp because logged out users can't communicate to ConsentHub
	if (isLoggedOutConsentToken) {
		isBannerVisible = shouldShowBannerForLoggedOutUser({
			consentToken,
			fallbackConsentToken,
		});
	} else {
		// Authed/logged in user flow
		isBannerVisible = shouldShowBannerForLoggedInUser({
			consentToken,
			fallbackConsentToken,
			consentTimestamp,
		});
	}

	return isBannerVisible;
};
